import { ActivityResults, ObjectiveKeyEnum } from 'models';
import { RowFormat, SCEligibility } from './table-generator';
import { ResultsByActivityAndSC, getProportion } from './TaxonomyTableGenerator.hooks';
import {
  calculateEnablingAdaptationSubstantialContribution,
  getEligibleAlignedAbsolute,
  getActivityName,
  getCode,
  getEligibleNotAlignedAbsolute,
  SubstantialContribution,
  calculateAdaptationOnlySubstantialContribution,
  calculateNonAlignedSubstantialContribution,
  calculateAlignedSubstantialContribution,
  checkEnabling,
  checkTransitional,
  getContribution,
} from './TaxonomyTableActivitiesUtils';

const calculateSubstantialContribution = (
  results: ActivityResults[],
  type: string,
  aligned: boolean,
  rowObjective: string,
  activityRef: string,
  total: number
): SubstantialContribution => {
  if (aligned === false) {
    return calculateNonAlignedSubstantialContribution(results, type, total);
  } else if (rowObjective === 'adaptationOnlyEnabling') {
    return calculateEnablingAdaptationSubstantialContribution(results, type, total);
  } else if (rowObjective === 'adaptationOnly') {
    return calculateAdaptationOnlySubstantialContribution(results, type, total);
  } else {
    return calculateAlignedSubstantialContribution(results, type, rowObjective, activityRef, total);
  }
};

export const getEligibleAlignedActivities = (
  results: ResultsByActivityAndSC,
  type: string,
  activities: any,
  total: number
): RowFormat[] => {
  const eligibleAlignedActivities: RowFormat[] = [];

  Object.entries(results).forEach(([activityRef, resultsBySC]) => {
    Object.entries(resultsBySC).forEach(([objective, activityResults]) => {
      if (
        ((objective === ObjectiveKeyEnum.adaptation || objective === 'adaptationEnabling') &&
          type === 'revenue') ||
        objective === 'notAligned'
      ) {
        return;
      }

      const alignedAbsolute = getEligibleAlignedAbsolute(activityResults, type, objective);
      const eligibleAbsolute = activityResults.reduce((sum, result) => {
        return sum + (result.cachedResult?.financials[type]?.eligible ?? 0);
      }, 0);

      if (alignedAbsolute !== 0) {
        const substantialContributions = calculateSubstantialContribution(
          activityResults,
          type,
          true,
          objective,
          activityRef,
          total
        );

        const nonAlignedSubstantialContributions = calculateSubstantialContribution(
          activityResults,
          type,
          false,
          objective,
          activityRef,
          total
        );

        const newRow: RowFormat = {
          activityName: getActivityName(activityRef, activities),
          code: getCode(activityRef, activities, substantialContributions, type, activityResults),
          eligiblePct: eligibleAbsolute,
          alignedAbsolute,
          alignedPct: getProportion(alignedAbsolute, total) ?? 0,
          mitigationSC: getContribution(
            substantialContributions.mitigation,
            nonAlignedSubstantialContributions.mitigation
          ),
          adaptationSC: getContribution(
            substantialContributions.adaptation,
            nonAlignedSubstantialContributions.adaptation
          ),
          waterSC: getContribution(
            substantialContributions.water,
            nonAlignedSubstantialContributions.water
          ),
          circularSC: getContribution(
            substantialContributions.circular,
            nonAlignedSubstantialContributions.circular
          ),
          pollutionSC: getContribution(
            substantialContributions.pollution,
            nonAlignedSubstantialContributions.pollution
          ),
          biodiversitySC: getContribution(
            substantialContributions.biodiversity,
            nonAlignedSubstantialContributions.biodiversity
          ),
          enabling: total ? checkEnabling(objective, alignedAbsolute) / total : 0,
          transitional: total ? checkTransitional(objective, alignedAbsolute) / total : 0,
          alignedPctOfEligible: 0, //calculated later,
        };
        eligibleAlignedActivities.push(newRow);
      }
    });
  });
  return eligibleAlignedActivities;
};

export const getEligibleNotAlignedActivities = (
  results: ResultsByActivityAndSC,
  type: string,
  activities: any,
  total: number
): RowFormat[] => {
  const eligibleNotAlignedActivities: RowFormat[] = [];
  Object.entries(results).forEach(([activityRef, resultsBySC]) => {
    Object.entries(resultsBySC).forEach(([objective, activityResults]) => {
      const absolute = getEligibleNotAlignedAbsolute(activityResults, type);

      if (absolute > 0) {
        const substantialContributions = calculateSubstantialContribution(
          activityResults,
          type,
          false,
          objective,
          activityRef,
          total
        );
        const newRow: RowFormat = {
          activityName: getActivityName(activityRef, activities),
          code: getCode(activityRef, activities, substantialContributions, type, activityResults),
          eligiblePct: absolute,
          alignedAbsolute: 0,
          alignedPct: 0,
          mitigationSC:
            objective === 'mitigation'
              ? substantialContributions.mitigation
              : { financial: 0, eligibility: SCEligibility.nonEligible },
          adaptationSC:
            objective === 'adaptation'
              ? substantialContributions.adaptation
              : { financial: 0, eligibility: SCEligibility.nonEligible },
          waterSC:
            objective === 'water'
              ? substantialContributions.water
              : { financial: 0, eligibility: SCEligibility.nonEligible },
          circularSC:
            objective === 'circular'
              ? substantialContributions.circular
              : { financial: 0, eligibility: SCEligibility.nonEligible },
          pollutionSC:
            objective === 'pollution'
              ? substantialContributions.pollution
              : { financial: 0, eligibility: SCEligibility.nonEligible },
          biodiversitySC:
            objective === 'biodiversity'
              ? substantialContributions.biodiversity
              : { financial: 0, eligibility: SCEligibility.nonEligible },
          enabling: undefined,
          transitional: undefined,
          alignedPctOfEligible: 0,
        };
        eligibleNotAlignedActivities.push(newRow);
      }
    });
  });
  return eligibleNotAlignedActivities;
};
